import CustomSelect from '@/components/shared/CustomSelect/CustomSelect';
import { isStaging } from '@/utils/isStaging';
import { IntegrationCategory } from '@/utils/integrations';

import { motion } from 'framer-motion';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CardSection from '../Partials/CardSection';
import { IntegrationsTabsSection } from '@/components/Integrations/integrations-tab-section';
import { IntegrationsList } from '@/components/Integrations/integrations-list';

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
};

export default function Configurations() {
  const { t } = useTranslation('integration');
  const items = [{ text: t('integrations'), link: '/integrations' }];
  const [activeSection, setActiveSection] = useState('all');

  const allIntegrations = IntegrationsList(isStaging);

  const sections = [
    {
      id: 1,
      label: t('all'),
      value: 'all',
    },
    {
      id: 2,
      label: t('memberArea'),
      value: 'memberArea',
    },
    {
      id: 3,
      label: t('tracking'),
      value: 'tracking',
    },
    {
      id: 4,
      label: t('automations'),
      value: 'automations',
    },
    {
      id: 5,
      label: t('vsl'),
      value: 'vsl',
    },
    {
      id: 6,
      label: t('sms'),
      value: 'sms',
    },
    {
      id: 7,
      label: t('globalEncapsulation'),
      value: 'globalEncapsulation',
    },
    {
      id: 8,
      label: t('invoices'),
      value: 'invoices',
    },
    {
      id: 9,
      label: t('webhooks'),
      value: 'webhooks',
    },
  ];

  const getIntegrationsByCategory = (category: IntegrationCategory) => {
    return allIntegrations.filter(
      (integration) => integration.category === category
    );
  };

  const renderIntegrationSection = (
    category: IntegrationCategory,
    title: string,
    description: string
  ) => (
    <motion.section
      className='mb-16'
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <h2 className='text-xl font-semibold'>{title}</h2>
      <p className='text-muted text-sm mt-4 mb-8'>{description}</p>
      <motion.div
        className='grid grid-cols-1 gap-8 md:gap-6 md:grid-cols-2 xl:grid-cols-3'
        variants={containerVariants}
        initial='hidden'
        animate='show'
      >
        {getIntegrationsByCategory(category).map((integration) => (
          <motion.div key={integration.route} variants={itemVariants}>
            <CardSection
              title={integration.name}
              icon={integration.icon}
              description={integration.description}
              disabled={integration.disabled}
              route={integration.route}
            />
          </motion.div>
        ))}
      </motion.div>
    </motion.section>
  );

  const renderSections = () => {
    switch (activeSection) {
      case 'all':
        return (
          <>
            {renderIntegrationSection(
              'memberArea',
              t('memberArea'),
              t('memberAreaDescription')
            )}
            {renderIntegrationSection(
              'tracking',
              t('tracking'),
              t('trackingDescription')
            )}
            {renderIntegrationSection(
              'automations',
              t('automations'),
              t('automationsDescription')
            )}
            {renderIntegrationSection('vsl', t('vsl'), t('vslDescription'))}
            {renderIntegrationSection('sms', t('sms'), t('smsDescription'))}
            {renderIntegrationSection(
              'globalEncapsulation',
              t('globalEncapsulation'),
              t('globalEncapsulationDescription')
            )}
            {renderIntegrationSection(
              'invoices',
              t('invoices'),
              t('invoicesDescription')
            )}
            {renderIntegrationSection(
              'webhooks',
              t('webhooks'),
              t('webhooksDescription')
            )}
          </>
        );

      case 'memberArea':
        return renderIntegrationSection(
          'memberArea',
          t('memberArea'),
          t('memberAreaDescription')
        );

      case 'tracking':
        return renderIntegrationSection(
          'tracking',
          t('tracking'),
          t('trackingDescription')
        );

      case 'automations':
        return renderIntegrationSection(
          'automations',
          t('automations'),
          t('automationsDescription')
        );

      case 'vsl':
        return renderIntegrationSection('vsl', t('vsl'), t('vslDescription'));

      case 'sms':
        return renderIntegrationSection('sms', t('sms'), t('smsDescription'));

      case 'globalEncapsulation':
        return renderIntegrationSection(
          'globalEncapsulation',
          t('globalEncapsulation'),
          t('globalEncapsulationDescription')
        );

      case 'invoices':
        return renderIntegrationSection(
          'invoices',
          t('invoices'),
          t('invoicesDescription')
        );

      case 'webhooks':
        return renderIntegrationSection(
          'webhooks',
          t('webhooks'),
          t('webhooksDescription')
        );

      default:
        return null;
    }
  };

  return (
    <IntegrationsTabsSection>
      <motion.section
        className='flex flex-col mt-4 gap-8 xl:gap-[112px] md:flex-row'
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <ul className='text-sm hidden md:block space-y-2 text-colorText text-ellipsis'>
          {sections.map((section) => (
            <li
              className={`px-2 py-1.5 cursor-pointer hover:bg-zinc-800/50 rounded-md transition-colors ${
                activeSection === section.value ? 'bg-zinc-800' : ''
              }`}
              key={section.id}
              onClick={() => setActiveSection(section.value)}
            >
              <h2>{section.label}</h2>
            </li>
          ))}
        </ul>

        <div className='md:hidden'>
          <CustomSelect
            placeholder=''
            options={sections}
            value={activeSection}
            onValueChange={(value) => setActiveSection(value)}
            defaultValue={activeSection}
          />
        </div>
        <div className='max-w-[768px] mx-auto'>{renderSections()}</div>
      </motion.section>
    </IntegrationsTabsSection>
  );
}
